import { create } from 'zustand';

type PanelContentType = 'group-details' | 'group-lifecycle' | 'help' | null;
type GroupMode = 'readonly' | 'edit' | 'create';

interface LabourGroupsPanelState {
  // Panel orchestration
  isOpen: boolean;
  contentType: PanelContentType;
  groupId: string | null;

  // Form/view state (preserved from existing)
  mode: GroupMode;
  editedGroupName: string;
  transferListSearch: string;

  // Methods
  openGroupDetails: (groupId: string) => void;
  openGroupCreate: () => void;
  openGroupEdit: (groupId: string, initialName?: string) => void;
  openHelp: () => void;
  switchMode: (mode: GroupMode) => void;
  closePanel: () => void;
  setEditedGroupName: (name: string) => void;
  setTransferListSearch: (search: string) => void;
  resetState: () => void;
}

export const useLabourGroupsPanelStore = create<LabourGroupsPanelState>(set => {
  const resetState = (): Omit<
    LabourGroupsPanelState,
    | 'openGroupDetails'
    | 'openGroupCreate'
    | 'openGroupEdit'
    | 'openHelp'
    | 'switchMode'
    | 'closePanel'
    | 'setEditedGroupName'
    | 'setTransferListSearch'
    | 'resetState'
  > => ({
    isOpen: false,
    contentType: null,
    groupId: null,
    mode: 'readonly',
    editedGroupName: '',
    transferListSearch: '',
  });

  return {
    ...resetState(),

    openGroupDetails: (groupId: string) =>
      set({
        isOpen: true,
        contentType: 'group-details',
        groupId,
        mode: 'readonly',
        editedGroupName: '',
        transferListSearch: '',
      }),

    openGroupCreate: () =>
      set({
        isOpen: true,
        contentType: 'group-lifecycle',
        groupId: null,
        mode: 'create',
        editedGroupName: '',
        transferListSearch: '',
      }),

    openGroupEdit: (groupId: string, initialName: string = '') =>
      set({
        isOpen: true,
        contentType: 'group-lifecycle',
        groupId,
        mode: 'edit',
        editedGroupName: initialName,
        transferListSearch: '',
      }),

    openHelp: () =>
      set({
        isOpen: true,
        contentType: 'help',
        groupId: null,
        mode: 'readonly',
        editedGroupName: '',
        transferListSearch: '',
      }),

    switchMode: (mode: GroupMode) => {
      set(state => {
        // When switching from details to edit, change content type
        if (state.contentType === 'group-details' && mode === 'edit') {
          return {
            contentType: 'group-lifecycle' as PanelContentType,
            mode,
            transferListSearch: '',
          };
        }
        // When switching from edit to readonly, change content type
        if (state.contentType === 'group-lifecycle' && mode === 'readonly') {
          return {
            contentType: 'group-details' as PanelContentType,
            mode,
            transferListSearch: '',
          };
        }
        return {
          mode,
          transferListSearch: '',
        };
      });
    },

    closePanel: () =>
      set({
        isOpen: false,
        contentType: null,
        groupId: null,
        mode: 'readonly',
        editedGroupName: '',
        transferListSearch: '',
      }),

    setEditedGroupName: (name: string) => set({ editedGroupName: name }),

    setTransferListSearch: (search: string) =>
      set({ transferListSearch: search }),

    resetState: () => set(resetState()),
  };
});

// Export backward compatible name for gradual migration
export const useGroupManagementStore = useLabourGroupsPanelStore;
